'use client';

import { useState, useEffect, memo, ReactNode, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User, LogOut, ChevronDown, Bot, Sparkles, Briefcase, Shield, ExternalLink } from 'lucide-react';
import { getAiAmpUrl, getMarketingUrl } from '@lykbl/config';
import { BrandLogo, type SiteContext } from './BrandLogo';

interface NavLink {
  label: string;
  href: string;
  internal?: boolean;
}

// Navigation configs per site (using functions for dynamic URLs)
const getNavConfig = (site: SiteContext) => {
  const aiAmpUrl = getAiAmpUrl('');
  const marketingUrl = getMarketingUrl('');

  if (site === 'marketing') {
    return {
      links: [
        { label: 'Home', href: '/', internal: true },
        { label: 'About', href: '/about', internal: true },
        { label: 'Creators', href: '/creators', internal: true },
        { label: 'Brands', href: '/brands', internal: true },
        { label: 'Blog', href: '/blog', internal: true },
      ],
      // Use environment-aware domains for cross-domain links.
      crossDomainLink: { label: 'Ai-AMP', href: aiAmpUrl, icon: Bot },
      primaryCta: { label: 'Get Started', href: '/auth?signup=true' },
    };
  }
  
  return {
    links: [
      { label: 'Marketplace', href: '/', internal: true },
      { label: 'Build', href: '/build', internal: true },
      { label: 'Developers', href: '/developers', internal: true },
      { label: 'Docs', href: '/docs', internal: true },
    ],
    // Use environment-aware domains for cross-domain links.
    crossDomainLink: { label: 'lykbl.com', href: marketingUrl, icon: Sparkles },
    primaryCta: { label: 'Get Started', href: '/auth?signup=true' },
  };
};

interface UserInfo {
  email?: string;
  role?: 'creator' | 'developer' | 'brand' | 'admin';
}

interface UnifiedNavbarProps {
  site: SiteContext;
  isAuthenticated?: boolean;
  user?: UserInfo | null;
  onSignOut?: () => void | Promise<void>;
  authLink?: string;
  /** Optional override for the primary CTA href (e.g. Foundation gating). */
  primaryCtaHref?: string;
  /** Optional override for the primary CTA label. */
  primaryCtaLabel?: string;
  children?: ReactNode;
}

const getRoleIcon = (role?: string) => {
  switch (role) {
    case 'creator': return Sparkles;
    case 'developer': return Bot;
    case 'brand': return Briefcase;
    case 'admin': return Shield;
    default: return User;
  }
};

const getConsoleRoute = (role?: string) => {
  const aiAmpUrl = getAiAmpUrl('');
  switch (role) {
    case 'creator': return `${aiAmpUrl}/console/creator`;
    case 'developer': return `${aiAmpUrl}/console/developer`;
    case 'brand': return `${aiAmpUrl}/console/brand`;
    case 'admin': return `${aiAmpUrl}/console/admin`;
    default: return `${aiAmpUrl}/app`;
  }
};

function UnifiedNavbarComponent({
  site,
  isAuthenticated = false,
  user,
  onSignOut,
  authLink = '/auth',
  primaryCtaHref,
  primaryCtaLabel,
}: UnifiedNavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  
  const config = useMemo(() => getNavConfig(site), [site]);
  const accentColor = site === 'ai-amp' ? '#00D4FF' : '#FF6B6B';
  const resolvedPrimaryCta = {
    href: primaryCtaHref ?? config.primaryCta.href,
    label: primaryCtaLabel ?? config.primaryCta.label,
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuOpen && !(e.target as HTMLElement).closest('[data-user-menu]')) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [userMenuOpen]);

  const RoleIcon = getRoleIcon(user?.role);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-ink/90 backdrop-blur-xl border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="container">
        <nav className="flex items-center justify-between h-16 md:h-20 gap-4">
          {/* Logo with site badge */}
          <Link to="/" className="relative z-10 shrink-0">
            <BrandLogo variant="light" site={site} showSiteBadge size="md" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1 flex-1 min-w-0 justify-center">
            {config.links.map((link: NavLink) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap shrink-0 ${
                  location.pathname === link.href
                    ? 'text-white bg-white/5'
                    : 'text-[#A8A8B3] hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Cross-domain link */}
            <a
              href={config.crossDomainLink.href}
              target="_blank"
              rel="noreferrer"
              className="px-4 py-2 text-sm font-medium rounded-lg transition-colors text-[#A8A8B3] hover:text-white hover:bg-white/5 flex items-center gap-1.5 whitespace-nowrap shrink-0"
            >
              <config.crossDomainLink.icon className="w-4 h-4 shrink-0" style={{ color: accentColor }} />
              {config.crossDomainLink.label}
            </a>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3 shrink-0">
            {isAuthenticated ? (
              <div className="relative" data-user-menu>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}99)` }}
                  >
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <span className="max-w-24 truncate">{user?.email?.split('@')[0] || 'Account'}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-56 bg-[#1a1a25] border-2 border-white/30 rounded-xl shadow-2xl overflow-hidden z-100 backdrop-blur-xl"
                    >
                      <div className="p-3 border-b border-white/20 bg-white/5">
                        <p className="text-xs text-[#A8A8B3] mb-1">Signed in as</p>
                        <p className="text-sm font-medium text-white truncate mb-2">{user?.email}</p>
                        {user?.role && (
                          <span
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border"
                            style={{ 
                              background: `${accentColor}20`, 
                              color: accentColor,
                              borderColor: `${accentColor}40`
                            }}
                          >
                            <RoleIcon className="w-3 h-3" />
                            {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          </span>
                        )}
                      </div>

                      <div className="p-2 bg-[#1a1a25]">
                        <a
                          href={getConsoleRoute(user?.role)}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-3 px-3 py-2.5 text-sm text-white hover:bg-white/10 rounded-lg transition-colors font-medium"
                        >
                          <RoleIcon className="w-4 h-4" style={{ color: accentColor }} />
                          {user?.role ? `${user.role.charAt(0).toUpperCase() + user.role.slice(1)} Console` : 'Dashboard'}
                        </a>
                        <a
                          href={`${getAiAmpUrl('')}/app`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-3 px-3 py-2.5 text-sm text-white hover:bg-white/10 rounded-lg transition-colors font-medium"
                        >
                          <Bot className="w-4 h-4 text-[#00D4FF]" />
                          Ai Marketplace
                        </a>
                      </div>

                      <div className="p-2 border-t border-white/20 bg-white/5">
                        <button
                          onClick={onSignOut}
                          className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-white hover:bg-red-500/20 hover:text-red-300 rounded-lg transition-colors font-medium"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <>
                <Link
                  to={authLink}
                  className="px-4 py-2.5 text-sm font-medium text-[#A8A8B3] hover:text-white transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to={resolvedPrimaryCta.href}
                  className="px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-300 hover:-translate-y-0.5"
                  style={{
                    background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
                    color: '#0a0a0f',
                    boxShadow: `0 4px 20px -4px ${accentColor}50`,
                  }}
                >
                  {resolvedPrimaryCta.label}
                </Link>
              </>
            )}
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-white z-10"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-ink border-t border-white/5"
          >
            <div className="container py-6 space-y-4">
              {config.links.map((link: NavLink) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`block px-4 py-3 text-lg font-medium rounded-xl transition-colors ${
                    location.pathname === link.href
                      ? 'text-white bg-white/5'
                      : 'text-[#A8A8B3] hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              <a
                href={config.crossDomainLink.href}
                className="flex items-center gap-2 px-4 py-3 text-lg font-medium text-[#A8A8B3] hover:text-white rounded-xl transition-colors"
              >
                <config.crossDomainLink.icon className="w-5 h-5" style={{ color: accentColor }} />
                {config.crossDomainLink.label}
                <ExternalLink className="w-4 h-4 ml-auto opacity-50" />
              </a>

              <div className="pt-4 border-t border-white/5 space-y-3">
                {isAuthenticated ? (
                  <>
                    <div className="px-4 py-3 bg-white/5 rounded-xl">
                      <p className="text-xs text-[#A8A8B3]">Signed in as</p>
                      <p className="text-sm font-medium text-white truncate">{user?.email}</p>
                    </div>
                    <a
                      href={getConsoleRoute(user?.role)}
                      className="flex items-center justify-center gap-2 px-4 py-3 text-lg font-medium text-white rounded-xl"
                      style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)` }}
                    >
                      <RoleIcon className="w-5 h-5" />
                      {user?.role ? `${user.role.charAt(0).toUpperCase() + user.role.slice(1)} Console` : 'Dashboard'}
                    </a>
                    <button
                      onClick={onSignOut}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 text-lg font-medium text-[#A8A8B3] hover:text-white rounded-xl hover:bg-white/5 transition-colors"
                    >
                      <LogOut className="w-5 h-5" />
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to={authLink}
                      className="block px-4 py-3 text-lg font-medium text-center text-[#A8A8B3] hover:text-white rounded-xl hover:bg-white/5 transition-colors"
                    >
                      Sign In
                    </Link>
                    <Link
                      to={config.primaryCta.href}
                      className="block px-4 py-3 text-lg font-medium text-center rounded-xl"
                      style={{
                        background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
                        color: '#0a0a0f',
                      }}
                    >
                      {config.primaryCta.label}
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export const UnifiedNavbar = memo(UnifiedNavbarComponent);
